import React, { CSSProperties, Fragment, ReactNode, useMemo } from 'react';

import { ISortStatuses } from '../../../hooks/types';
import { getRandomString } from '../../../utils';
import { Preloader } from '..';
import { SortArrows } from './SortArrows';
import {
  CellWrapper,
  HeadSeparator,
  TableBodyItem,
  TableBodyRow,
  TableBodyWrapper,
  TableHeadItem,
  TableHeadRow,
  TableHeadText,
  TableHeadWrapper,
  TableWrapper
} from './style';

type HeadProps = {
  label?: ReactNode;
  width: number;
  name?: string;
  tooltip?: ReactNode;
  sortFunc?: (name: string) => unknown;
  separator?: boolean;
};

export type TableProps = {
  renderRow: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    index: number
  ) => {
    main: ReactNode[];
    extra?: ReactNode;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowStyles?: (prop: any) => CSSProperties;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableData?: any[];
  head: HeadProps[];
  noDataContent?: ReactNode;
  fetching?: boolean;
  sortStatuses?: ISortStatuses;
  isScrollable?: boolean;
  renderRowDeps?: Array<unknown>;
};

const Table = ({
  renderRow,
  renderRowDeps = [],
  rowStyles,
  tableData,
  head,
  sortStatuses,
  fetching,
  noDataContent,
  isScrollable = false
}: TableProps): JSX.Element => {
  const renderContent = useMemo(() => {
    return (
      <>
        {tableData?.map((row, i) => {
          const { main, extra } = renderRow(row, i);
          return (
            <Fragment key={getRandomString()}>
              <TableBodyRow style={rowStyles?.(row)} data-test-id={`table-row-${i}`}>
                {main.map((item, index) => {
                  return (
                    <CellWrapper
                      width={head[index].width}
                      key={getRandomString()}
                      data-test-id={`table-cell-${head[index].name}`}>
                      <TableBodyItem>{item}</TableBodyItem>
                    </CellWrapper>
                  );
                })}
              </TableBodyRow>
              {extra && <TableBodyRow>{extra}</TableBodyRow>}
            </Fragment>
          );
        })}
      </>
    );
  }, [tableData, rowStyles, ...renderRowDeps]);

  return (
    <TableWrapper isScrollable={isScrollable}>
      <TableHeadWrapper>
        <TableHeadRow>
          {head.map((item) => {
            const { name, width, separator = true, label, tooltip, sortFunc } = item;

            let textPadding: number;
            if (!!tooltip && !!sortFunc) {
              textPadding = 3;
            } else {
              textPadding = !!tooltip || !!sortFunc ? 2 : 1;
            }

            return (
              <TableHeadItem separator={separator} width={width} key={getRandomString()}>
                <TableHeadText padding={textPadding}>{label}</TableHeadText>
                {tooltip}
                {!!sortFunc && (
                  <SortArrows
                    collumnName={name}
                    sortStatus={sortStatuses}
                    onClick={() => {
                      sortFunc(name as string);
                    }}
                  />
                )}
                {separator && <HeadSeparator />}
              </TableHeadItem>
            );
          })}
        </TableHeadRow>
      </TableHeadWrapper>
      <TableBodyWrapper isScrollable={isScrollable}>
        {!!fetching && <Preloader />}
        {(tableData?.length === 0 || !tableData) && !fetching && noDataContent}
        {tableData?.length !== 0 && !fetching && renderContent}
      </TableBodyWrapper>
    </TableWrapper>
  );
};

export default Table;
